<template>
    <div class="ds-wrap h-full w-full flex flex-col text-left">
        <div class="ds-toolbar">
            <div class="ds-toolbar__row">
                <div class="ds-filters">
                    <div class="ds-search">
                        <search ref="searchRef" @on-search="onSearch" class-list="px-3 h-9"></search>
                    </div>
                    <div>
                        <label class="ds-filter__label">Status</label>
                        <select v-model="statusFilter" class="ds-select" @change="onFilterChange">
                            <option value="">All</option>
                            <option value="subscribed">Subscribed</option>
                            <option value="open_access">Open Access</option>
                        </select>
                    </div>
                </div>
                <div class="ds-toolbar__actions">
                    <button type="button" class="ds-export-btn" :disabled="isExportExcel" @click="generateReport">
                        <font-awesome-icon :icon="['fas', 'file-excel']" class="ds-export-btn__icon" />
                        <span>{{ isExportExcel ? "Exporting..." : "Export Excel" }}</span>
                    </button>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--primary" @click="openCreate">
                        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                        Add database
                    </button>
                </div>
            </div>
        </div>

        <div class="flex-auto flex flex-col px-4 sm:px-8 pb-8">
            <div v-if="loading" class="flex-auto flex flex-col">
                <table-shimmer :titleHeaders="tableHeaders" :rows="perPage" />
            </div>

            <div v-else-if="!loading && items.length === 0" class="flex-auto flex justify-center items-center py-16">
                <div class="text-center max-w-md px-4">
                    <div class="ds-empty-icon">📚</div>
                    <h2 class="text-xl font-semibold text-gray-800 mb-2">No databases found</h2>
                    <p class="text-gray-500 mb-4">Try adjusting filters, or add a new library database.</p>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--primary" @click="openCreate">
                        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                        Add database
                    </button>
                </div>
            </div>

            <template v-else>
                <div class="ds-table-shell overflow-x-auto">
                    <table class="min-w-full text-left text-sm">
                        <thead>
                            <tr>
                                <th class="ds-th w-12">#</th>
                                <th class="ds-th">Name</th>
                                <th class="ds-th">Status</th>
                                <th class="ds-th">Access</th>
                                <th class="ds-th">Scope</th>
                                <th class="ds-th">AI</th>
                                <th class="ds-th">Shared With</th>
                                <th v-if="canManage" class="ds-th text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in items" :key="item.id" class="ds-row">
                                <td class="ds-td text-gray-400 font-medium">{{ rowNumber(index) }}</td>
                                <td class="ds-td font-medium">
                                    <div>{{ item.name || "—" }}</div>
                                    <div v-if="item.subscription_label" class="text-xs text-gray-500 mt-1">
                                        {{ item.subscription_label }}
                                    </div>
                                </td>
                                <td class="ds-td">
                                    <span class="ds-badge" :class="item.status === 'open_access' ? 'ds-badge--open' : 'ds-badge--sub'">
                                        {{ formatStatus(item.status) }}
                                    </span>
                                </td>
                                <td class="ds-td">{{ item.access || "—" }}</td>
                                <td class="ds-td">
                                    {{ item.scope === "campus" ? ("Campus · " + (item.campus || "—")) : "Shared" }}
                                </td>
                                <td class="ds-td">{{ item.has_ai ? "Yes" : "No" }}</td>
                                <td class="ds-td">{{ item.shared_with || "—" }}</td>
                                <td v-if="canManage" class="ds-td text-right whitespace-nowrap">
                                    <button v-if="canUpdate" type="button" title="Edit" class="ds-icon-btn" @click="openEdit(item)">
                                        <font-awesome-icon icon="pen" class="w-3 h-3 text-gray-700" />
                                    </button>
                                    <button v-if="canDelete" type="button" title="Delete" class="ds-icon-btn ds-icon-btn--danger" @click="confirmDelete(item)">
                                        <font-awesome-icon icon="trash" class="w-3 h-3 text-gray-700" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="mt-4" v-if="paginatorInfoWithItems && paginatorInfoWithItems.total">
                    <pagination :paginatorInfo="paginatorInfoWithItems" :setPage="setPage"></pagination>
                </div>
            </template>
        </div>

        <transition name="fade">
            <div
                v-if="showFormModal"
                class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4"
                @click.self="closeForm"
            >
                <div class="ds-form bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-screen overflow-y-auto">
                    <div class="ds-form__header">
                        <h3 class="text-lg font-semibold text-gray-900">
                            {{ editingId ? "Edit database" : "Add database" }}
                        </h3>
                        <p class="text-sm text-gray-500 mt-1">{{ entity }}<span v-if="campus"> · {{ campus }}</span></p>
                    </div>

                    <div class="ds-form__body grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div class="sm:col-span-2">
                            <label class="ds-label">Name <span class="text-red-500">*</span></label>
                            <input v-model="form.name" type="text" class="ds-input" placeholder="Database name" />
                        </div>
                        <div class="sm:col-span-2">
                            <label class="ds-label">Description</label>
                            <textarea v-model="form.description" rows="4" class="ds-input ds-textarea" placeholder="Description"></textarea>
                        </div>
                        <div>
                            <label class="ds-label">Access</label>
                            <input v-model="form.access" type="text" class="ds-input" placeholder="ADPOLY Library Portal" />
                        </div>
                        <div>
                            <label class="ds-label">Subscription Label</label>
                            <input v-model="form.subscription_label" type="text" class="ds-input" placeholder="Subscribed" />
                        </div>
                        <div>
                            <label class="ds-label">Status</label>
                            <select v-model="form.status" class="ds-select ds-select--full">
                                <option value="subscribed">Subscribed</option>
                                <option value="open_access">Open Access</option>
                            </select>
                        </div>
                        <div>
                            <label class="ds-label">Shared With</label>
                            <input v-model="form.shared_with" type="text" class="ds-input" placeholder="FCHS LIBRARY" />
                        </div>
                        <div>
                            <label class="ds-label">Scope</label>
                            <select v-model="form.scope" class="ds-select ds-select--full">
                                <option value="shared">Shared (institution-wide)</option>
                                <option value="campus">Campus</option>
                            </select>
                        </div>
                        <div v-if="form.scope === 'campus'">
                            <label class="ds-label">Campus <span class="text-red-500">*</span></label>
                            <input v-model="form.campus" type="text" class="ds-input" placeholder="AA" />
                        </div>
                        <div class="flex items-center pt-6">
                            <label class="inline-flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                                <input v-model="form.has_ai" type="checkbox" class="ds-checkbox" />
                                Has AI features
                            </label>
                        </div>
                    </div>

                    <div class="ds-form__footer">
                        <button type="button" class="ds-btn ds-btn--ghost" @click="closeForm">Cancel</button>
                        <base-button type="button" :disabled="saving" class="bg-teal-700 hover:bg-teal-800 text-white" @click="saveForm">
                            {{ saving ? "Saving…" : "Save" }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import gql from "graphql-tag";
import Pagination from "../../../../components/shared/pagination.vue";
import TableShimmer from "../../../../components/shared/TableShimmer.vue";
import Search from "../../../../components/shared/search.vue";
import exportManagerMixin from "../../facility/modules/exportManagerMixin.js";

const LIST_QUERY = gql`
    query LibraryDatabases(
        $first: Int!
        $page: Int!
        $entity: String
        $campus: String
        $search: String
        $status: String
        $scopeFilter: String
    ) {
        libraryDatabases(
            first: $first
            page: $page
            entity: $entity
            campus: $campus
            search: $search
            status: $status
            scopeFilter: $scopeFilter
        ) {
            error
            error_code
            message
            data {
                id
                entity
                scope
                campus
                name
                description
                access
                subscription_label
                status
                has_ai
                shared_with
                created_at
                updated_at
            }
            paginatorInfo {
                currentPage
                lastPage
                perPage
                total
                hasMorePages
                firstItem
                lastItem
            }
        }
    }
`;

const CREATE_MUTATION = gql`
    mutation CreateLibraryDatabase($input: LibraryDatabaseInput!) {
        createLibraryDatabase(input: $input) {
            error
            error_code
            message
        }
    }
`;

const UPDATE_MUTATION = gql`
    mutation UpdateLibraryDatabase($id: ID!, $input: LibraryDatabaseInput!) {
        updateLibraryDatabase(id: $id, input: $input) {
            error
            error_code
            message
        }
    }
`;

const DELETE_MUTATION = gql`
    mutation DeleteLibraryDatabase($id: ID!) {
        deleteLibraryDatabase(id: $id) {
            error
            error_code
            message
        }
    }
`;

function emptyForm() {
    return {
        name: "",
        description: "",
        access: "",
        subscription_label: "",
        status: "subscribed",
        has_ai: false,
        shared_with: "",
        scope: "shared",
        campus: "",
    };
}

export default {
    name: "DatabasesModule",
    mixins: [exportManagerMixin],
    components: { Pagination, TableShimmer, Search },
    props: {
        entity: { type: String, required: true },
        campus: { type: String, default: "" },
    },
    emits: ["changed"],
    data() {
        return {
            items: [],
            paginatorInfo: null,
            loading: false,
            isExportExcel: false,
            perPage: 25,
            page: 1,
            search: "",
            statusFilter: "",
            showFormModal: false,
            editingId: null,
            saving: false,
            form: emptyForm(),
            tableHeaders: ["#", "Name", "Status", "Access", "Scope", "AI", "Shared With", "Actions"],
        };
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        permissions() {
            return this.$store.getters.permissions;
        },
        canCreate() {
            return this.hasPermission("library-databases-create");
        },
        canUpdate() {
            return this.hasPermission("library-databases-update");
        },
        canDelete() {
            return this.hasPermission("library-databases-delete");
        },
        canManage() {
            return this.canUpdate || this.canDelete;
        },
        paginatorInfoWithItems() {
            if (!this.paginatorInfo) return null;
            var pi = this.paginatorInfo;
            var firstItem = pi.firstItem || (pi.currentPage - 1) * pi.perPage + 1;
            var lastItem = pi.lastItem || Math.min(pi.currentPage * pi.perPage, pi.total);
            return {
                currentPage: pi.currentPage,
                lastPage: pi.lastPage,
                perPage: pi.perPage,
                total: pi.total,
                hasMorePages: pi.hasMorePages,
                firstItem: firstItem,
                lastItem: lastItem,
            };
        },
    },
    created() {
        this.loadList();
    },
    methods: {
        hasPermission(name) {
            if (this.role === "superadmin") return true;
            return this.permissions && this.permissions.includes(name);
        },
        formatStatus(status) {
            if (status === "open_access") return "Open Access";
            if (status === "subscribed") return "Subscribed";
            return status || "—";
        },
        listQueryVariables(page, first) {
            return {
                first: first,
                page: page,
                entity: this.entity,
                campus: this.campus || null,
                search: this.search || null,
                status: this.statusFilter || null,
                scopeFilter: null,
            };
        },
        onFilterChange() {
            this.page = 1;
            this.loadList();
        },
        onSearch(value) {
            this.search = value || "";
            this.page = 1;
            this.loadList();
        },
        setPage(page) {
            this.page = page;
            this.loadList();
        },
        rowNumber(index) {
            if (!this.paginatorInfo) return index + 1;
            var first = this.paginatorInfo.firstItem || 1;
            return first + index;
        },
        toastAlert(options) {
            if (!this.$swal) return Promise.resolve({});
            var merged = Object.assign({ timer: 2000 }, options);
            if (
                (merged.icon === "error" || merged.icon === "warning") &&
                (options.timer === undefined || options.timer === null)
            ) {
                merged.timer = 3500;
            }
            var self = this;
            var Toast = this.$swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timerProgressBar: true,
                didOpen: function (toast) {
                    toast.addEventListener("mouseenter", self.$swal.stopTimer);
                    toast.addEventListener("mouseleave", self.$swal.resumeTimer);
                },
            });
            return Toast.fire(merged);
        },
        loadList() {
            var self = this;
            this.loading = true;
            this.$apollo
                .query({
                    query: LIST_QUERY,
                    variables: this.listQueryVariables(this.page, this.perPage),
                    fetchPolicy: "network-only",
                })
                .then(function (result) {
                    var data = result && result.data ? result.data : null;
                    var payload = data && data.libraryDatabases ? data.libraryDatabases : null;
                    if (payload && !payload.error) {
                        self.items = payload.data || [];
                        self.paginatorInfo = payload.paginatorInfo || null;
                    } else {
                        self.items = [];
                        self.paginatorInfo = null;
                        self.toastAlert({
                            icon: "error",
                            title: (payload && payload.message) || "Failed to load databases",
                        });
                    }
                })
                .catch(function (err) {
                    self.items = [];
                    self.paginatorInfo = null;
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Failed to load databases",
                    });
                })
                .finally(function () {
                    self.loading = false;
                });
        },
        openCreate() {
            this.editingId = null;
            this.form = emptyForm();
            if (this.campus) this.form.campus = this.campus;
            this.showFormModal = true;
        },
        openEdit(item) {
            this.editingId = item.id;
            this.form = {
                name: item.name || "",
                description: item.description || "",
                access: item.access || "",
                subscription_label: item.subscription_label || "",
                status: item.status || "subscribed",
                has_ai: !!item.has_ai,
                shared_with: item.shared_with || "",
                scope: item.scope || "shared",
                campus: item.campus || "",
            };
            this.showFormModal = true;
        },
        closeForm() {
            this.showFormModal = false;
            this.editingId = null;
            this.form = emptyForm();
        },
        buildInput() {
            var scope = this.form.scope || "shared";
            return {
                entity: this.entity,
                scope: scope,
                campus: scope === "campus" ? this.form.campus || null : null,
                name: this.form.name || "",
                description: this.form.description || null,
                access: this.form.access || null,
                subscription_label: this.form.subscription_label || null,
                status: this.form.status || "subscribed",
                has_ai: !!this.form.has_ai,
                shared_with: this.form.shared_with || null
            };
        },
        saveForm() {
            var self = this;
            if (!this.form.name || !String(this.form.name).trim()) {
                this.toastAlert({ icon: "warning", title: "Name is required" });
                return;
            }
            if (this.form.scope === "campus" && (!this.form.campus || !String(this.form.campus).trim())) {
                this.toastAlert({ icon: "warning", title: "Campus is required when scope is campus" });
                return;
            }
            this.saving = true;
            var input = this.buildInput();
            var request = this.editingId
                ? this.$apollo.mutate({
                      mutation: UPDATE_MUTATION,
                      variables: { id: this.editingId, input: input },
                  })
                : this.$apollo.mutate({
                      mutation: CREATE_MUTATION,
                      variables: { input: input },
                  });

            request
                .then(function (res) {
                    var data = res && res.data ? res.data : null;
                    var payload = self.editingId
                        ? data && data.updateLibraryDatabase
                        : data && data.createLibraryDatabase;
                    if (payload && !payload.error) {
                        self.toastAlert({
                            icon: "success",
                            title: payload.message || "Saved successfully",
                        });
                        self.closeForm();
                        self.loadList();
                        self.$emit("changed");
                    } else {
                        self.toastAlert({
                            icon: "error",
                            title: (payload && payload.message) || "Save failed",
                        });
                    }
                })
                .catch(function (err) {
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Save failed",
                    });
                })
                .finally(function () {
                    self.saving = false;
                });
        },
        confirmDelete(item) {
            var self = this;
            if (!this.$swal) return;
            this.$swal
                .fire({
                    title: "Delete this database?",
                    html:
                        "<div class='text-left text-sm text-gray-600'><div><strong>Name:</strong> " +
                        (item.name || "—") +
                        "</div></div>",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#dc2626",
                    cancelButtonColor: "#6b7280",
                    confirmButtonText: "Yes, delete",
                })
                .then(function (result) {
                    if (result && result.isConfirmed) self.deleteItem(item);
                });
        },
        deleteItem(item) {
            var self = this;
            this.$apollo
                .mutate({ mutation: DELETE_MUTATION, variables: { id: item.id } })
                .then(function (res) {
                    var data = res && res.data ? res.data : null;
                    var payload =
                        data && data.deleteLibraryDatabase ? data.deleteLibraryDatabase : null;
                    if (payload && !payload.error) {
                        self.toastAlert({
                            icon: "success",
                            title: payload.message || "Deleted",
                        });
                        if (self.items.length === 1 && self.page > 1) self.page = self.page - 1;
                        self.loadList();
                        self.$emit("changed");
                    } else {
                        self.toastAlert({
                            icon: "error",
                            title: (payload && payload.message) || "Delete failed",
                        });
                    }
                })
                .catch(function (err) {
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Delete failed",
                    });
                });
        },
        exportFileName() {
            return "ADPOLY_" + (this.campus || "ALL") + "_Library_Databases";
        },
        exportSheetName() {
            return "Databases";
        },
        _getExportQuery: function () {
            return { query: LIST_QUERY, root: "libraryDatabases" };
        },
        _snapshotExportVariables: function () {
            var entity = this.entity;
            var campus = this.campus || null;
            var search = this.search || null;
            var status = this.statusFilter || null;
            return function (page, limit) {
                return {
                    first: limit,
                    page: page,
                    entity: entity,
                    campus: campus,
                    search: search,
                    status: status,
                    scopeFilter: null,
                };
            };
        },
        mapExportRow(item) {
            return {
                Name: item.name || "",
                Description: item.description || "",
                Access: item.access || "",
                "Subscription Label": item.subscription_label || "",
                Status: item.status || "",
                "Has AI": item.has_ai ? "Yes" : "No",
                "Shared With": item.shared_with || "",
                Scope: item.scope || "",
                Campus: item.campus || "",
            };
        },
    },
};
</script>

<style lang="scss" scoped>
.ds-wrap {
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 40%);
}
.ds-toolbar {
    padding: 1.25rem 1rem 1rem;
}
@media (min-width: 640px) {
    .ds-toolbar {
        padding-left: 2rem;
        padding-right: 2rem;
    }
}
.ds-toolbar__row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 0.75rem 1rem;
}
.ds-toolbar__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
}
.ds-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.75rem;
    flex: 1 1 auto;
}
.ds-filter__label {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
    margin-bottom: 0.25rem;
}
.ds-select,
.ds-input {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    padding: 0.45rem 0.75rem;
    font-size: 0.875rem;
    background: #ffffff;
    color: #111827;
}
.ds-select {
    min-width: 8.5rem;
    height: 2.25rem;
}
.ds-select--full {
    min-width: 0;
    height: auto;
}
.ds-textarea {
    resize: vertical;
    min-height: 5rem;
}
.ds-select:focus,
.ds-input:focus {
    outline: none;
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.15);
}
.ds-search {
    flex: 1 1 14rem;
    min-width: 12rem;
    max-width: 22rem;
}
.ds-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    padding: 0.55rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
}
.ds-btn--primary {
    background: #0f766e;
    color: #ffffff;
    border: 1px solid #0f766e;
}
.ds-btn--primary:hover {
    background: #115e59;
}
.ds-btn--ghost {
    background: #ffffff;
    color: #374151;
    border: 1px solid #d1d5db;
}
.ds-export-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    white-space: nowrap;
    border-radius: 0.75rem;
    border: 1px solid rgba(16, 185, 129, 0.25);
    background: linear-gradient(to bottom, #ffffff, rgba(16, 185, 129, 0.08));
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #052e1f;
}
.ds-export-btn:disabled {
    pointer-events: none;
    opacity: 0.6;
}
.ds-export-btn__icon {
    font-size: 1.05rem;
    color: #059669;
}
.ds-checkbox {
    width: 1rem;
    height: 1rem;
    accent-color: #0f766e;
}
.ds-table-shell {
    border: 1px solid #e5e7eb;
    border-radius: 0.75rem;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.ds-th {
    padding: 0.75rem 1rem;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #ffffff;
    background: #0f766e;
    white-space: nowrap;
}
.ds-th:first-child {
    border-top-left-radius: 0.7rem;
}
.ds-th:last-child {
    border-top-right-radius: 0.7rem;
}
.ds-row:nth-child(even) {
    background: #f0fdfa;
}
.ds-row:hover {
    background: #ccfbf1;
}
.ds-td {
    padding: 0.7rem 1rem;
    color: #1f2937;
    border-top: 1px solid #e5e7eb;
    vertical-align: middle;
}
.ds-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.15rem 0.55rem;
    border-radius: 9999px;
    font-size: 0.72rem;
    font-weight: 700;
}
.ds-badge--sub {
    background: #ecfdf5;
    color: #047857;
}
.ds-badge--open {
    background: #eff6ff;
    color: #1d4ed8;
}
.ds-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 0.5rem;
    background: #f3f4f6;
    margin-left: 0.35rem;
}
.ds-icon-btn:hover {
    background: #e0e7ff;
}
.ds-icon-btn--danger:hover {
    background: #fee2e2;
}
.ds-empty-icon {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
}
.ds-form__header {
    padding: 1.25rem 1.5rem 0.75rem;
    border-bottom: 1px solid #f3f4f6;
}
.ds-form__body {
    padding: 1.25rem 1.5rem;
}
.ds-form__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 1rem 1.5rem 1.25rem;
    border-top: 1px solid #f3f4f6;
}
.ds-label {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.35rem;
}
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
